/**
 * Compact viewer HUD for phones, tablets and narrow windows: one bottom bar
 * (Leave · Chat · Emote · camera mode · More) instead of the desktop layout's
 * top buttons, centre camera pill and bottom-left capture row, which collide
 * on small screens. Less-used tools (screenshot, record, guestbook, VR…) live
 * in the More sheet.
 */
import React, { useEffect, useState } from 'react';
import { Ellipsis, LogOut, Eye, MessageSquare, PersonStanding, Smile, ZoomIn } from 'lucide-react';
import { isTouchDevice } from './touchControls';
import './ViewerHudBar.css';

const NARROW = '(max-width: 768px)';

/** True when the viewer should use the compact (bottom bar) HUD. */
export function useCompactHud() {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(NARROW);
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return isTouchDevice || narrow;
}

export type HudCamMode = 'normal' | 'zoom' | 'firstPerson';

export interface HudTool {
  key: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  title?: string;
  tone?: 'accent' | 'danger' | 'warn';
  disabled?: boolean;
  /** Keep the sheet open after the tap (e.g. a toggle whose state you want to see). */
  keepOpen?: boolean;
}

const CAM_MODES: [HudCamMode, string, React.ReactNode][] = [
  ['normal', 'Normal', <PersonStanding key="n" size={20} />],
  ['zoom', 'Zoom', <ZoomIn key="z" size={20} />],
  ['firstPerson', '1st person', <Eye key="f" size={20} />],
];

export const ViewerHudBar: React.FC<{
  onBack: () => void;
  chatOpen: boolean;
  /** Omit for a world with no chat room (marketplace previews). */
  onToggleChat?: () => void;
  /** Avatar play: emote + camera controls. */
  hasAvatar: boolean;
  emotesOpen: boolean;
  onToggleEmotes: () => void;
  camMode: HudCamMode;
  onCamMode: (m: HudCamMode) => void;
  tools: HudTool[];
  /** Highlights the More button (e.g. unread guestbook notes). */
  moreBadge?: boolean;
  modeLabel: string;
  /** Extra sheet content (the Enter VR button, which hides itself when unsupported). */
  sheetExtra?: React.ReactNode;
}> = ({ onBack, chatOpen, onToggleChat, hasAvatar, emotesOpen, onToggleEmotes, camMode, onCamMode, tools, moreBadge, modeLabel, sheetExtra }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const stop = (e: React.PointerEvent | React.TouchEvent) => e.stopPropagation();

  return <>
    {moreOpen && <div className="gx-hud-scrim" onClick={() => setMoreOpen(false)} />}
    {moreOpen && (
      <div className="gx-hud-sheet" data-hud-sheet role="menu" aria-label="More" onPointerDown={stop}>
        <div className="gx-hud-sheet__head">{modeLabel}</div>
        <div className="gx-hud-sheet__grid">
          {tools.map((t) => (
            <button
              key={t.key}
              role="menuitem"
              className={`gx-hud-sheet__item${t.tone ? ` is-${t.tone}` : ''}`}
              title={t.title}
              disabled={t.disabled}
              onClick={() => { t.onClick(); if (!t.keepOpen) setMoreOpen(false); }}
            >
              {t.icon}<span>{t.label}</span>
            </button>
          ))}
        </div>
        {sheetExtra && <div className="gx-hud-sheet__extra">{sheetExtra}</div>}
      </div>
    )}
    <nav className="gx-hud-bar" aria-label="World controls" onPointerDown={stop} onTouchStart={stop}>
      <button className="gx-hud-btn" onClick={onBack} aria-label="Leave world">
        <LogOut size={20} /><span>Leave</span>
      </button>
      {onToggleChat && (
        <button className={`gx-hud-btn${chatOpen ? ' on' : ''}`} onClick={onToggleChat} aria-pressed={chatOpen}>
          <MessageSquare size={20} /><span>Chat</span>
        </button>
      )}
      {hasAvatar && (
        <button className={`gx-hud-btn${emotesOpen ? ' on' : ''}`} onClick={onToggleEmotes} aria-pressed={emotesOpen} aria-label="Emotes">
          <Smile size={20} /><span>Emote</span>
        </button>
      )}
      {hasAvatar && (
        <div className="gx-hud-seg" role="radiogroup" aria-label="Camera">
          {CAM_MODES.map(([mode, label, icon]) => (
            <button
              key={mode}
              role="radio"
              aria-checked={camMode === mode}
              className={`gx-hud-btn${camMode === mode ? ' sel' : ''}`}
              onClick={() => onCamMode(mode)}
            >
              {icon}<span>{label}</span>
            </button>
          ))}
        </div>
      )}
      <button className={`gx-hud-btn${moreOpen ? ' on' : ''}${moreBadge ? ' has-badge' : ''}`} onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen} aria-label="More">
        <Ellipsis size={20} /><span>More</span>
      </button>
    </nav>
  </>;
};
